import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Button, Typography, ButtonGroup } from '@mui/material';

export default function FittableImage({ 
  element, 
  updateElement, 
  ElementControls, 
  isInZone = false, 
  layoutId = null, 
  zoneId = null, 
  onElementClick = null 
}) {
  // Make frame responsive - handle both pixel values and percentage
  const getFrameWidth = (styleWidth) => {
    if (styleWidth === "100%" || styleWidth === "auto") {
      return "100%"; // Let CSS handle responsive width
    }
    if (typeof styleWidth === 'number') return styleWidth;
    if (typeof styleWidth === 'string') {
      const numValue = parseInt(styleWidth, 10);
      return isNaN(numValue) ? 400 : numValue;
    }
    return 400;
  };
  
  const getFrameHeight = (styleHeight, frameWidth) => {
    if (styleHeight === "auto" || styleHeight === "100%") {
      // For responsive frames, use a reasonable aspect ratio
      return frameWidth === "100%" ? 300 : 300;
    }
    if (typeof styleHeight === 'number') return styleHeight;
    if (typeof styleHeight === 'string') {
      const numValue = parseInt(styleHeight, 10);
      return isNaN(numValue) ? 300 : numValue;
    }
    return 300;
  };
  
  const baseFrameWidth = getFrameWidth(element?.styles?.width);
  const baseFrameHeight = getFrameHeight(element?.styles?.height, baseFrameWidth);
  const isResponsiveWidth = baseFrameWidth === "100%";

  const [imageSrc, setImageSrc] = useState(element?.imageSrc || '');
  const [imagePosition, setImagePosition] = useState(element?.imagePosition || { x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(element?.imageScale || 1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(!element?.imageSrc);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const [currentFrameHeight, setCurrentFrameHeight] = useState(() => {
    // Initialize with element height or default height
    if (element?.styles?.height) {
      if (typeof element.styles.height === 'number') return element.styles.height;
      if (typeof element.styles.height === 'string') {
        const numValue = parseInt(element.styles.height, 10);
        return isNaN(numValue) ? 300 : numValue;
      }
    }
    return baseFrameHeight;
  });
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const currentImagePos = useRef(imagePosition);

  // Preset frame dimensions
  const getPresetDimensions = (preset) => {
    const baseWidth = isResponsiveWidth ? 400 : (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400);
    
    switch (preset) {
      case 'square':
        return { width: baseWidth, height: baseWidth };
      case '16:9':
        return { width: baseWidth, height: Math.round(baseWidth * 9 / 16) };
      case '9:16':
        return { width: baseWidth, height: Math.round(baseWidth * 16 / 9) };
      default:
        return { width: baseWidth, height: baseFrameHeight };
    }
  };

  const handlePresetChange = (preset) => {
    const dimensions = getPresetDimensions(preset);
    const newHeight = dimensions.height;
    
    setCurrentFrameHeight(newHeight);
    
    // Update element with new dimensions
    updateElement(element.id, {
      styles: {
        ...element.styles,
        width: isResponsiveWidth ? "100%" : dimensions.width,
        height: newHeight
      }
    }, isInZone, layoutId, zoneId);
  };

  // Update state when element prop changes
  useEffect(() => {
    if (element?.imageSrc) {
      setImageSrc(element.imageSrc);
      setIsEditing(false);
    }
    if (element?.imagePosition) {
      setImagePosition(element.imagePosition);
    }
    if (element?.imageScale !== undefined) {
      setImageScale(element.imageScale);
    }
  }, [element]);

  // Update refs when position changes
  useEffect(() => {
    currentImagePos.current = imagePosition;
  }, [imagePosition]);

  // Update local frame height when element changes (but not during resize)
  useEffect(() => {
    if (element?.styles?.height && !isResizing) {
      const heightValue = typeof element.styles.height === 'number' 
        ? element.styles.height 
        : parseInt(element.styles.height, 10) || baseFrameHeight;
      setCurrentFrameHeight(heightValue);
    }
  }, [element?.styles?.height, isResizing, baseFrameHeight]);

  // Load image dimensions when imageSrc changes
  useEffect(() => {
    if (imageSrc) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = imageSrc;
    }
  }, [imageSrc]);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
        setIsEditing(false);
        setImagePosition({ x: 0, y: 0 });
        
        // Load image to get dimensions and set appropriate scale
        const img = new Image();
        img.onload = () => {
          const imgWidth = img.naturalWidth;
          const imgHeight = img.naturalHeight;
          
          setImageDimensions({ width: imgWidth, height: imgHeight });
          
          // Calculate scale to fit frame height
          const frameHeight = baseFrameHeight;
          const frameWidth = isResponsiveWidth ? 400 : (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400);
          
          const scaleForHeight = frameHeight / imgHeight;
          const scaleForWidth = frameWidth / imgWidth;
          
          // Use the smaller scale to fit within frame bounds
          const initialScale = Math.min(scaleForHeight, scaleForWidth, 1);
          
          setImageScale(initialScale);
          
          // Update the element with the new image and calculated scale
          updateElement(element.id, {
            imageSrc: e.target.result,
            imagePosition: { x: 0, y: 0 },
            imageScale: initialScale
          }, isInZone, layoutId, zoneId);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }, [element.id, updateElement, isInZone, layoutId, zoneId, baseFrameHeight, baseFrameWidth, isResponsiveWidth]);

  const handleMouseDown = useCallback((e) => {
    if (!imageSrc || isEditing || isResizing) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    
    // Store the offset between mouse and image position
    dragStartPos.current = {
      x: e.clientX - currentImagePos.current.x,
      y: e.clientY - currentImagePos.current.y
    };
    
    setDragStart({
      x: e.clientX - currentImagePos.current.x,
      y: e.clientY - currentImagePos.current.y
    });
  }, [imageSrc, isEditing, isResizing]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    
    // Calculate new position based on mouse position and initial offset
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Update position immediately for smooth dragging
    const newPosition = { x: newX, y: newY };
    setImagePosition(newPosition);
    currentImagePos.current = newPosition;
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      
      // Save the final position to the element
      updateElement(element.id, {
        imagePosition: currentImagePos.current,
        imageScale
      }, isInZone, layoutId, zoneId);
    }
  }, [isDragging, imageScale, element.id, updateElement, isInZone, layoutId, zoneId]);

  const handleWheel = useCallback((e) => {
    if (!imageSrc || isEditing) return;
    
    e.preventDefault();
    const scaleChange = e.deltaY > 0 ? -0.1 : 0.1;
    
    // Calculate minimum scale to ensure image fits frame height
    let minScale = 0.1;
    if (imageDimensions.width && imageDimensions.height) {
      const currentHeight = isResizing ? currentFrameHeight : baseFrameHeight;
      const frameWidth = isResponsiveWidth ? 
        (containerRef.current ? containerRef.current.offsetWidth : 400) : 
        (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400);
      
      // Calculate scale needed to fit frame height
      const scaleForHeight = currentHeight / imageDimensions.height;
      // Calculate scale needed to fit frame width  
      const scaleForWidth = frameWidth / imageDimensions.width;
      
      // Use the smaller scale to ensure image doesn't exceed frame dimensions when scaled down
      minScale = Math.min(scaleForHeight, scaleForWidth, 1);
      minScale = Math.max(0.1, minScale); // Ensure minimum of 0.1 for very small frames
    }
    
    const newScale = Math.max(minScale, Math.min(3, imageScale + scaleChange));
    setImageScale(newScale);
    
    // Update element with new scale
    updateElement(element.id, {
      imageScale: newScale,
      imagePosition: currentImagePos.current
    }, isInZone, layoutId, zoneId);
  }, [imageSrc, isEditing, imageScale, element.id, updateElement, isInZone, layoutId, zoneId, imageDimensions, isResizing, currentFrameHeight, baseFrameHeight, isResponsiveWidth, baseFrameWidth]);

  // Resize functionality
  const handleResizeStart = useCallback((direction, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStartData({
      mouseY: e.clientY,
      height: currentFrameHeight
    });
  }, [currentFrameHeight]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStartData) return;
    
    e.preventDefault();
    
    const deltaY = e.clientY - resizeStartData.mouseY;
    
    // For full-width fittable images, only change height
    let newHeight = resizeStartData.height;
    
    // Calculate height change based on resize direction
    if (resizeDirection === 'se' || resizeDirection === 'sw') {
      // Bottom corners - increase height
      newHeight = Math.max(50, resizeStartData.height + deltaY);
    } else if (resizeDirection === 'ne' || resizeDirection === 'nw') {
      // Top corners - decrease height
      newHeight = Math.max(50, resizeStartData.height - deltaY);
    }
    
    // Apply max height limit
    newHeight = Math.min(800, newHeight);
    
    // Update local state for immediate visual feedback
    setCurrentFrameHeight(newHeight);
    
    // Don't update element during resize move to prevent re-renders
    // We'll update it on resize end instead
  }, [isResizing, resizeStartData, resizeDirection]);

  const handleResizeEnd = useCallback(() => {
    const finalHeight = currentFrameHeight;
    
    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);
    
    // Update element with final height
    updateElement(element.id, {
      styles: {
        ...element.styles,
        width: isResponsiveWidth ? "100%" : element.styles?.width || 300,
        height: finalHeight
      }
    }, isInZone, layoutId, zoneId);
  }, [currentFrameHeight, element.id, element.styles, updateElement, isInZone, layoutId, zoneId, isResponsiveWidth]);

  // Global mouse events for dragging and resizing
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = (e) => handleMouseUp(e);
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      
      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Global mouse events for resizing
  useEffect(() => {
    if (isResizing) {
      const handleGlobalResizeMove = (e) => handleResizeMove(e);
      const handleGlobalResizeEnd = (e) => handleResizeEnd(e);
      
      document.addEventListener('mousemove', handleGlobalResizeMove);
      document.addEventListener('mouseup', handleGlobalResizeEnd);
      
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalResizeMove);
        document.removeEventListener('mouseup', handleGlobalResizeEnd);
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const handleEdit = () => setIsEditing(true);

  const handleReset = () => {
    const resetPos = { x: 0, y: 0 };
    setImagePosition(resetPos);
    setImageScale(1);
    currentImagePos.current = resetPos;
    updateElement(element.id, {
      imagePosition: resetPos,
      imageScale: 1
    }, isInZone, layoutId, zoneId);
  };

  const renderResizeHandle = (direction, style) => (
    <Box
      sx={{
        position: 'absolute',
        width: 12,
        height: 12,
        backgroundColor: '#2630f3',
        border: '2px solid #ffffff',
        borderRadius: '50%',
        cursor: 'ns-resize',
        opacity: 0.8,
        '&:hover': {
          opacity: 1,
          transform: 'scale(1.2)'
        },
        transition: 'all 0.2s',
        zIndex: 10,
        ...style
      }}
      onMouseDown={(e) => handleResizeStart(direction, e)}
    />
  );

  // Calculate the height to use, ensuring it's always a valid number
  const calculateHeight = () => {
    if (isResizing && typeof currentFrameHeight === 'number' && currentFrameHeight > 0) {
      return Math.max(50, currentFrameHeight);
    }
    if (typeof baseFrameHeight === 'number' && baseFrameHeight > 0) {
      return Math.max(50, baseFrameHeight);
    }
    return 300; // Fallback height
  };
  
  const finalHeight = calculateHeight();

  return (
    <Box
      sx={{
        position: 'relative',
        width: isResponsiveWidth ? '100%' : baseFrameWidth,
        height: finalHeight, // Use number directly
        minHeight: 50, // Ensure minimum height
        ...(isResponsiveWidth ? {} : { maxWidth: `${baseFrameWidth}px` }),
        border: '2px dashed #ccc',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#f9f9f9',
        transition: isResizing ? 'none' : 'height 0.2s ease',
        cursor: isDragging ? 'grabbing' : isResizing ? 'ns-resize' : (imageSrc && !isEditing ? 'grab' : 'default'),
        userSelect: 'none',
        mx: isResponsiveWidth ? 0 : 'auto', // Center only if not full width
        '&:hover .resize-handles': {
          opacity: 1
        }
      }}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      title={imageSrc && !isEditing ? "Click and drag to position • Scroll to zoom • Drag top/bottom handles to resize height" : undefined}
    >
      {!imageSrc || isEditing ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 2
          }}
        >
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
            Fittable Image Frame
          </Typography>
          
          {/* Preset Frame Options */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' } }}>
              Frame Presets:
            </Typography>
            <ButtonGroup size="small" variant="outlined" sx={{ mb: 1 }}>
              <Button
                onClick={() => handlePresetChange('square')}
                sx={{ 
                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 0.3, md: 0.5 }
                }}
                title="Square frame (1:1 ratio)"
              >
                Square
              </Button>
              <Button
                onClick={() => handlePresetChange('16:9')}
                sx={{ 
                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 0.3, md: 0.5 }
                }}
                title="Landscape frame (16:9 ratio - good for videos)"
              >
                16:9
              </Button>
              <Button
                onClick={() => handlePresetChange('9:16')}
                sx={{ 
                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 0.3, md: 0.5 }
                }}
                title="Portrait frame (9:16 ratio - good for mobile)"
              >
                9:16
              </Button>
            </ButtonGroup>
            <Typography variant="caption" color="textSecondary" sx={{ 
              fontSize: { xs: '0.5rem', md: '0.6rem' },
              opacity: 0.8,
              textAlign: 'center'
            }}>
              Current: {isResponsiveWidth ? '100%' : (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400)} × {finalHeight}
            </Typography>
          </Box>
          
          <label>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button variant="contained" component="span" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
              Upload Image
            </Button>
          </label>
          <Typography variant="caption" color="textSecondary" sx={{ 
            textAlign: 'center', 
            px: 1,
            fontSize: { xs: '0.6rem', md: '0.75rem' },
            lineHeight: 1.3
          }}>
            Upload an image, then click and drag to position. Drag top/bottom handles to resize height.
          </Typography>
        </Box>
      ) : (
        <>
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Fittable content"
            style={{
              position: 'absolute',
              left: imagePosition.x,
              top: imagePosition.y,
              transform: `scale(${imageScale})`,
              transformOrigin: 'top left',
              maxWidth: 'none',
              maxHeight: 'none',
              pointerEvents: 'none', // Prevents image from interfering with drag
            }}
            draggable={false} // Prevents browser's native image drag
          />
          
          {/* Controls overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 4, md: 8 },
              left: { xs: 4, md: 8 },
              right: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              alignItems: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
              backgroundColor: 'rgba(0,0,0,0.7)',
              borderRadius: 1,
              p: { xs: 0.5, md: 1 },
              pointerEvents: 'auto' // Allow controls to be clickable
            }}
            onMouseDown={(e) => e.stopPropagation()} // Prevent drag when clicking controls
          >
            {/* Frame Preset Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePresetChange('square')}
                sx={{ 
                  fontSize: { xs: '0.5rem', md: '0.6rem' },
                  py: { xs: 0.2, md: 0.3 },
                  px: { xs: 0.5, md: 0.8 },
                  color: 'white',
                  borderColor: 'white',
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
                title="Square frame"
              >
                1:1
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePresetChange('16:9')}
                sx={{ 
                  fontSize: { xs: '0.5rem', md: '0.6rem' },
                  py: { xs: 0.2, md: 0.3 },
                  px: { xs: 0.5, md: 0.8 },
                  color: 'white',
                  borderColor: 'white',
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
                title="Landscape frame"
              >
                16:9
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePresetChange('9:16')}
                sx={{ 
                  fontSize: { xs: '0.5rem', md: '0.6rem' },
                  py: { xs: 0.2, md: 0.3 },
                  px: { xs: 0.5, md: 0.8 },
                  color: 'white',
                  borderColor: 'white',
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
                title="Portrait frame"
              >
                9:16
              </Button>
            </Box>
            
            {/* Main Control Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleEdit}
                sx={{ 
                  fontSize: { xs: '0.6rem', md: '0.7rem' }, 
                  py: { xs: 0.3, md: 0.5 },
                  px: { xs: 1, md: 1.5 }
                }}
              >
                Change Image
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleReset}
                sx={{ 
                  fontSize: { xs: '0.6rem', md: '0.7rem' }, 
                  py: { xs: 0.3, md: 0.5 },
                  px: { xs: 1, md: 1.5 },
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Resize handles - only show when not editing */}
      {!isEditing && (
        <Box className="resize-handles" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
          {/* Top-center - for height resizing */}
          {renderResizeHandle('ne', { top: -6, left: '50%', transform: 'translateX(-50%)' })}
          {/* Bottom-center - for height resizing */}
          {renderResizeHandle('se', { bottom: -6, left: '50%', transform: 'translateX(-50%)' })}
        </Box>
      )}
    </Box>
  );
} 